import { useCallback, useMemo, useState } from "react";
import { scopeKey } from "newui/features/RolesPermissions";
import { SCOPE_TYPE_VALUES } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import type {
	ScopeExclusion,
	ScopeOption,
	ScopeType,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export interface PendingDowngrade {
	/** The higher-order option the admin just picked. */
	option: ScopeOption;
	/** In-session selections it would deselect (rule 2). */
	covered: ScopeOption[];
	/**
	 * F4 — in-session selections that MIGHT be covered by `option` but this
	 * cannot be verified, because their real parent is deliberately withheld
	 * by the server (a bidder/hybrid company's invited `project`/`rfx` rows —
	 * PRO-719, a bidder must not see the inviting procurer's project
	 * structure). `confirmDowngrade()` never removes these on a guess:
	 * silently discarding a selection that turns out NOT to have been covered
	 * would be actual data loss, worse than leaving a possibly-redundant
	 * selection in place (the server's own containment check at preview/apply
	 * time is authoritative and safe either way — CONTRACT §11).
	 */
	uncertain: ScopeOption[];
}

export interface UseScopeSelectionResult {
	/** In-session picks (CONTRACT §3.2's `selectedScopes` pattern). */
	selectedScopes: ScopeOption[];
	/** Full cross-type set sent as `exclude[]` on every list request (§3.2/§3.5). */
	exclude: ScopeExclusion[];
	isSelected: (option: ScopeOption) => boolean;
	/** Toggle a row. Selecting a higher-order item that covers existing picks opens
	 * the rule-2 confirmation instead of applying immediately. */
	toggle: (option: ScopeOption) => void;
	remove: (option: ScopeOption) => void;
	/** Reports the CURRENT full set of already-granted rows a list has ever shown
	 * (not just the latest page) — folded into `exclude[]` per §3.5/rule 1, but
	 * never toggleable and never a rule-2 downgrade source (the admin didn't pick
	 * these this session). */
	registerAlreadyGranted: (type: ScopeType, options: ScopeOption[]) => void;
	pendingDowngrade: PendingDowngrade | null;
	confirmDowngrade: () => void;
	cancelDowngrade: () => void;
	/**
	 * Clears every in-progress pick back to the empty state. `AddAccessFlow`
	 * calls this on each fresh open so a previous target's picks never leak
	 * into a new session (D10) — the hook has no other way to signal "start
	 * over", since a re-render alone does not reset `useState`.
	 */
	reset: () => void;
}

const emptyAlreadyGranted = (): Record<ScopeType, ScopeOption[]> => ({
	global: [],
	project_type: [],
	project: [],
	rfx: [],
});

const keyOf = (option: ScopeOption): string => scopeKey(option.scope_type, option.scope_id);

const sameOptionSet = (a: ScopeOption[], b: ScopeOption[]): boolean => {
	if (a.length !== b.length) {
		return false;
	}
	const aKeys = new Set(a.map(keyOf));
	return b.every((option) => aKeys.has(keyOf(option)));
};

/**
 * Rule 1: a candidate is covered by an existing pick iff the pick is its
 * DIRECT parent. `ScopeOption.parent` only carries one level of ancestry (the
 * server's breadcrumb, not a full chain), so this deliberately does not catch
 * a 2-level-deep containment (e.g. picking a project_type after an rfx whose
 * immediate parent is a project, not that project_type) — the server's own
 * `ScopeResolver::contains()` still enforces full containment at preview/apply
 * time (CONTRACT §11), so nothing incorrect can be SUBMITTED; this only means
 * the in-session "this will deselect…" warning (rule 2) can miss a multi-level
 * case. Flagged as a known, deliberate scope simplification.
 */
const isDirectChildOf = (child: ScopeOption, parent: ScopeOption): boolean =>
	child.parent !== null &&
	child.parent.scope_type === parent.scope_type &&
	child.parent.scope_id === parent.scope_id;

/**
 * F4 — `project`/`rfx` rows for a bidder/hybrid company's invited RFx tree
 * arrive with `parent: null` even though they DO have a real parent — the
 * server deliberately withholds it (PRO-719: a bidder must not see the
 * inviting procurer's project structure; confirmed intentional and staying,
 * not a bug to route around). That is different from `global`/`project_type`
 * rows, whose `parent: null` is genuine — there is nothing above them to
 * disclose. Only the former is "opaque": a null that hides real ancestry
 * rather than reporting there is none.
 */
const hasOpaqueParent = (option: ScopeOption): boolean =>
	option.parent === null && (option.scope_type === "project" || option.scope_type === "rfx");

const scopeTypeRank = (type: ScopeType): number => SCOPE_TYPE_VALUES.indexOf(type);

/** True iff `option` is a broader scope type than `other` (so it structurally
 * COULD be an ancestor of it, pending actual ancestry confirmation). */
const isBroaderType = (option: ScopeOption, other: ScopeOption): boolean =>
	scopeTypeRank(option.scope_type) < scopeTypeRank(other.scope_type);

// D8-T2 — owns the Add flow's in-progress scope selection: what's picked, what
// that implies for exclude[] (§3.2 + §3.5), and the rule-2 downgrade gate.
export const useScopeSelection = (): UseScopeSelectionResult => {
	const [selectedScopes, setSelectedScopes] = useState<ScopeOption[]>([]);
	const [alreadyGranted, setAlreadyGranted] =
		useState<Record<ScopeType, ScopeOption[]>>(emptyAlreadyGranted);
	const [pendingDowngrade, setPendingDowngrade] = useState<PendingDowngrade | null>(null);

	const isSelected = useCallback(
		(option: ScopeOption) => selectedScopes.some((s) => keyOf(s) === keyOf(option)),
		[selectedScopes]
	);

	const remove = useCallback((option: ScopeOption) => {
		setSelectedScopes((prev) => prev.filter((s) => keyOf(s) !== keyOf(option)));
	}, []);

	const toggle = useCallback(
		(option: ScopeOption) => {
			// Defensive — ScopePickerList already disables already-granted rows (rule 4).
			if (option.already_granted) {
				return;
			}

			const alreadySelected = selectedScopes.some((s) => keyOf(s) === keyOf(option));
			if (alreadySelected) {
				remove(option);
				return;
			}

			const covered = selectedScopes.filter((s) => isDirectChildOf(s, option));
			const coveredKeys = new Set(covered.map(keyOf));
			// F4 — a selection with an opaque (withheld) parent can never be proven
			// covered by isDirectChildOf() above, since its `parent` is null — but it
			// can't be proven UNCOVERED either. Rule 2 must not silently skip these
			// rows just because the server declined to disclose ancestry; flag them
			// distinctly instead of folding them into (or dropping them from) `covered`.
			const uncertain = selectedScopes.filter(
				(s) => !coveredKeys.has(keyOf(s)) && hasOpaqueParent(s) && isBroaderType(option, s)
			);

			if (covered.length > 0 || uncertain.length > 0) {
				setPendingDowngrade({ option, covered, uncertain });
				return;
			}

			setSelectedScopes((prev) => [...prev, option]);
		},
		[selectedScopes, remove]
	);

	const confirmDowngrade = useCallback(() => {
		if (!pendingDowngrade) {
			return;
		}
		// F4 — only `covered` (verified) rows are removed here. `uncertain` rows
		// are deliberately left in `selectedScopes` untouched: we cannot prove
		// they're actually contained by `pendingDowngrade.option`, so removing
		// them would risk silently discarding a selection the admin still needs.
		const coveredKeys = new Set(pendingDowngrade.covered.map(keyOf));
		setSelectedScopes((prev) => [
			...prev.filter((s) => !coveredKeys.has(keyOf(s))),
			pendingDowngrade.option,
		]);
		setPendingDowngrade(null);
	}, [pendingDowngrade]);

	const cancelDowngrade = useCallback(() => {
		setPendingDowngrade(null);
	}, []);

	const reset = useCallback(() => {
		setSelectedScopes([]);
		setAlreadyGranted(emptyAlreadyGranted());
		setPendingDowngrade(null);
	}, []);

	const registerAlreadyGranted = useCallback((type: ScopeType, options: ScopeOption[]) => {
		setAlreadyGranted((prev) =>
			sameOptionSet(prev[type], options) ? prev : { ...prev, [type]: options }
		);
	}, []);

	const exclude = useMemo(() => {
		const byKey = new Map<string, ScopeExclusion>();
		selectedScopes.forEach((option) => {
			byKey.set(keyOf(option), { scope_type: option.scope_type, scope_id: option.scope_id });
		});
		Object.values(alreadyGranted)
			.flat()
			.forEach((option) => {
				byKey.set(keyOf(option), { scope_type: option.scope_type, scope_id: option.scope_id });
			});
		return Array.from(byKey.values());
	}, [selectedScopes, alreadyGranted]);

	return {
		selectedScopes,
		exclude,
		isSelected,
		toggle,
		remove,
		registerAlreadyGranted,
		pendingDowngrade,
		confirmDowngrade,
		cancelDowngrade,
		reset,
	};
};
